import React, { useEffect, useMemo, useState } from "react"
import Select from "../select/select"
import Button from "../button/button"
import Popup from "../popup/popup"
import { MdModeEdit } from "react-icons/md"
import { agent } from "../../apiClient/api"
import { useSnackbar } from "../../context/snackbarContext"
import styles from "../../asset/styles/quotation/quotation.module.scss"
import { IQuotationRow, IRemarkItem } from "./quotationListTypes"
import { formatRemarkDate, getRemarksFromDetailsResponse } from "./quotationListUtil"

type Props = {
  isOpen: boolean
  quotation: IQuotationRow | null
  remarksByQuotation: Record<string, IRemarkItem[]>
  onRemarksByQuotationChange: React.Dispatch<React.SetStateAction<Record<string, IRemarkItem[]>>>
  onClose: () => void
}

const QuotationRemarksPopup: React.FC<Props> = ({
  isOpen,
  quotation,
  remarksByQuotation,
  onRemarksByQuotationChange,
  onClose,
}) => {
  const { showError, showSuccess } = useSnackbar()
  const [newRemarkText, setNewRemarkText] = useState("")
  const [editingRemarkId, setEditingRemarkId] = useState<string | null>(null)
  const [isRemarksLoading, setIsRemarksLoading] = useState(false)
  const [isRemarksSaving, setIsRemarksSaving] = useState(false)
  const [remarksSortDirection, setRemarksSortDirection] = useState<"asc" | "desc">("desc")

  const handleClose = () => {
    setNewRemarkText("")
    setEditingRemarkId(null)
    onClose()
  }

  const fetchRemarksForQuotation = async (quotationId: string) => {
    setIsRemarksLoading(true)
    try {
      const response = await agent.QuotationDetailsAPI.get(quotationId, true)
      const normalized = getRemarksFromDetailsResponse(response)
      onRemarksByQuotationChange((prev) => ({ ...prev, [quotationId]: normalized }))
    } catch (error) {
      showError("Failed to load remarks")
    } finally {
      setIsRemarksLoading(false)
    }
  }

  useEffect(() => {
    const quotationId = quotation?.quotation_id
    if (!isOpen || !quotationId) return
    if (Object.prototype.hasOwnProperty.call(remarksByQuotation, quotationId)) return
    fetchRemarksForQuotation(quotationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, quotation?.quotation_id, remarksByQuotation])

  const handleSaveRemark = async () => {
    const quotationId = quotation?.quotation_id
    const text = newRemarkText.trim()
    if (!quotationId || !text) return

    const currentList = remarksByQuotation[quotationId] || []
    const updatedList = editingRemarkId
      ? currentList.map((item) =>
          String(item.quotation_remark_id || "") === editingRemarkId ? { ...item, remark: text } : item,
        )
      : [
          ...currentList,
          {
            quotation_remark_id: null,
            remark: text,
            sequence_number: Number(currentList.length + 1),
          },
        ]

    const payloadRemarks = updatedList.map((item, index) => ({
      quotation_remark_id: item.quotation_remark_id ?? null,
      remark: item.remark,
      sequence_number: Number(index + 1),
    }))

    setIsRemarksSaving(true)
    try {
      const response = await agent.QuotationBasicDetailsAPI.put(
        {
          quotation_id: quotationId,
          remarks: payloadRemarks,
        } as any,
        true,
      )
      if (response?.status === false) {
        showError(response?.error?.message || "Failed to save remark")
        return
      }
      await fetchRemarksForQuotation(quotationId)
      setNewRemarkText("")
      setEditingRemarkId(null)
      showSuccess(editingRemarkId ? "Remark updated successfully" : "Remark added successfully")
    } catch (error: any) {
      showError(error?.message || "Failed to save remark")
    } finally {
      setIsRemarksSaving(false)
    }
  }

  const sortedRemarks = useMemo(() => {
    const quotationId = quotation?.quotation_id || ""
    const list = remarksByQuotation[quotationId] || []
    return [...list].sort((a, b) => {
      const seqA = Number(a.sequence_number || 0)
      const seqB = Number(b.sequence_number || 0)
      return remarksSortDirection === "asc" ? seqA - seqB : seqB - seqA
    })
  }, [remarksByQuotation, quotation, remarksSortDirection])

  return (
    <Popup
      isOpen={isOpen}
      onClose={handleClose}
      title={`Remarks - ${quotation?.quotation_name || "Quotation"}`}
      heightFiexd={true}
    >
      <div className={styles.remarksPopupBody}>
        <div className={styles.remarksHeaderRow}>
          <div className={styles.sortSelectWrap}>
            <Select
              label=""
              value={remarksSortDirection}
              onChange={(event) => setRemarksSortDirection((event.target.value as "asc" | "desc") || "desc")}
              options={[
                { label: "Newest First", value: "desc" },
                { label: "Oldest First", value: "asc" },
              ]}
              width="170px"
              height="34px"
            />
          </div>
        </div>
        <div className={styles.remarksList}>
          {isRemarksLoading ? (
            <div className={styles.emptyRemarks}>Loading remarks...</div>
          ) : sortedRemarks.length === 0 ? (
            <div className={styles.emptyRemarks}>No remarks yet.</div>
          ) : (
            sortedRemarks.map((item, index) => (
              <div key={`${item.quotation_remark_id || "new"}-${index}`} className={styles.remarkItem}>
                <span className={styles.remarkIndex}>{index + 1}.</span>
                <div className={styles.remarkContent}>
                  <span className={styles.remarkText}>{item.remark}</span>
                  <div className={styles.remarkMeta}>
                    <span>CREATED AT: {formatRemarkDate(item.created_at)}</span>
                    <span>CREATED BY: {String(item.created_by || "-").toUpperCase()}</span>
                  </div>
                  <div className={styles.remarkMeta}>
                    <span>LAST UPDATED BY: {String(item.updated_by || item.created_by || "-").toUpperCase()}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.remarkEditBtn}
                  onClick={() => {
                    setEditingRemarkId(String(item.quotation_remark_id || ""))
                    setNewRemarkText(item.remark)
                  }}
                  aria-label="Edit remark"
                  title="Edit remark"
                >
                  <MdModeEdit />
                </button>
              </div>
            ))
          )}
        </div>
        <div className={styles.remarksComposer}>
          <textarea
            className={styles.remarksTextarea}
            value={newRemarkText}
            onChange={(event) => setNewRemarkText(event.target.value)}
            rows={2}
            autoFocus={true}
            placeholder="Add a new remark..."
          />
          <Button
            type="contained"
            label={editingRemarkId ? "Update" : "Add"}
            onClick={handleSaveRemark}
            disabled={isRemarksSaving || newRemarkText.trim().length === 0}
            style={{ minWidth: "88px", height: "40px" }}
          />
        </div>
      </div>
    </Popup>
  )
}

export default QuotationRemarksPopup
